import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { useAuth } from '@/contexts/AuthContext';
import {
  enrollerAdminService,
  type MetabaseResourceFlat,
  type DashboardItem,
} from '@/services/ea/enrollerAdminService';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import CreateDashboardModal from './components/CreateDashboardModal';
import { Plus, Loader2, ChevronsUpDown, Check } from 'lucide-react';

const REFRESH_BUFFER_MS = 60_000;

const Dashboard = () => {
  useEffect(() => {
    document.title = 'Dashboard | Admin';
  }, []);

  const { user, token } = useAuth();
  const { handleError } = useErrorHandler();

  // Flat unified resources from /metabase/resources/flat
  const [flatResources, setFlatResources] = useState<MetabaseResourceFlat[]>([]);
  const [selectedCompositeKey, setSelectedCompositeKey] = useState<string>('');
  const [isLoadingResources, setIsLoadingResources] = useState(false);

  // Custom dashboards (for CreateDashboardModal refresh)
  const [savedDashboards, setSavedDashboards] = useState<DashboardItem[]>([]);

  // Shared iframe
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [isLoadingIframe, setIsLoadingIframe] = useState(false);

  // Combobox open state
  const [comboOpen, setComboOpen] = useState(false);

  // Modal
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Auto-refresh timer
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  // Update service context
  useEffect(() => {
    if (user && token) {
      enrollerAdminService.updateContext({ userId: user.id, token });
    }
  }, [user, token]);

  // Fetch flat resources and auto-select first
  useEffect(() => {
    const fetchResources = async () => {
      if (!user || !token) return;
      setIsLoadingResources(true);
      try {
        const items = await enrollerAdminService.fetchMetabaseResourcesFlat();
        setFlatResources(items);
        if (items.length > 0) {
          setSelectedCompositeKey(items[0].compositeKey);
        }
      } catch (error) {
        handleError(error, 'Failed to load analytics resources');
      } finally {
        setIsLoadingResources(false);
      }
    };
    fetchResources();
  }, [user, token]);

  // Load iframe when selection changes
  const loadIframeForResource = useCallback(
    async (resource: MetabaseResourceFlat) => {
      if (!user) return;
      setIframeUrl(null);
      setIsLoadingIframe(true);
      clearRefreshTimer();

      try {
        const response = await enrollerAdminService.getMetabaseToken({
          type: resource.resourceType,
          id: resource.resourceId,
          user_email: user.email ?? '',
        });

        if (response?.token) {
          const url = enrollerAdminService.buildMetabaseEmbedUrl(resource.resourceType, response.token);
          setIframeUrl(url);

          if (response.expires_at_epoch_ms) {
            const delay = response.expires_at_epoch_ms - Date.now() - REFRESH_BUFFER_MS;
            if (delay > 0) {
              refreshTimerRef.current = setTimeout(() => {
                loadIframeForResource(resource);
              }, delay);
            }
          }
        } else {
          setIframeUrl(null);
        }
      } catch (error) {
        handleError(error, 'Failed to load dashboard');
        setIframeUrl(null);
      } finally {
        setIsLoadingIframe(false);
      }
    },
    [user, clearRefreshTimer, handleError]
  );

  // Trigger load when selected composite key changes
  useEffect(() => {
    if (!selectedCompositeKey || flatResources.length === 0) return;
    const resource = flatResources.find((r) => r.compositeKey === selectedCompositeKey);
    if (resource) {
      loadIframeForResource(resource);
    }
  }, [selectedCompositeKey, flatResources]);

  const handleResourceChange = (compositeKey: string) => {
    setSelectedCompositeKey(compositeKey);
  };

  // Refresh saved dashboards list after creation (for CreateDashboardModal)
  const refreshSavedDashboards = async () => {
    try {
      const items = await enrollerAdminService.getDashboardItems();
      setSavedDashboards(items);
    } catch (error) {
      console.error('Failed to refresh saved dashboards', error);
    }
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => clearRefreshTimer();
  }, [clearRefreshTimer]);

  const selectedResource = flatResources.find((r) => r.compositeKey === selectedCompositeKey);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Dashboard</h1>
          <p className="text-muted-foreground">View analytics and insights</p>
        </div>
        <Button size="sm" onClick={() => setShowCreateModal(true)}>
          <Plus className="mr-1 h-4 w-4" />
          Create New Dashboard
        </Button>
      </div>

      {/* Dashboard Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Select Analytics Dashboard</CardTitle>
        </CardHeader>
        <CardContent>
          <Popover open={comboOpen} onOpenChange={setComboOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={comboOpen}
                disabled={isLoadingResources || flatResources.length === 0}
                className="w-full justify-between font-normal h-auto min-h-10 py-2"
              >
                <span className="truncate text-left flex-1 mr-2">
                  {isLoadingResources
                    ? 'Loading resources...'
                    : selectedCompositeKey
                    ? flatResources.find((r) => r.compositeKey === selectedCompositeKey)?.displayName
                    : 'Select a dashboard...'}
                </span>
                <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search dashboards..." />
                <CommandList>
                  <CommandEmpty>No dashboard found.</CommandEmpty>
                  <CommandGroup>
                    {flatResources.map((item) => (
                      <CommandItem
                        key={item.compositeKey}
                        value={item.displayName}
                        onSelect={() => {
                          handleResourceChange(item.compositeKey);
                          setComboOpen(false);
                        }}
                        className="items-start"
                      >
                        <Check
                          className={`mr-2 mt-0.5 h-4 w-4 shrink-0 ${
                            selectedCompositeKey === item.compositeKey ? 'opacity-100' : 'opacity-0'
                          }`}
                        />
                        <span className="break-words whitespace-normal line-clamp-4">{item.displayName}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </CardContent>
      </Card>

      {/* Loading indicator */}
      {isLoadingIframe && (
        <div className="flex items-center justify-center py-8 text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading dashboard...
        </div>
      )}

      {/* Iframe Area */}
      {iframeUrl && (
        <Card>
          <CardContent className="p-0 overflow-auto">
            <iframe
              src={iframeUrl}
              width="100%"
              className="rounded-lg w-full h-[calc(100vh-200px)] max-h-[calc(100vh-200px)] min-h-[600px]"
              title={selectedResource?.displayName ?? 'Dashboard'}
              scrolling="auto"
            />
          </CardContent>
        </Card>
      )}

      {!iframeUrl && !isLoadingIframe && selectedCompositeKey && (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            No dashboard data available
          </CardContent>
        </Card>
      )}

      {/* Create Dashboard Modal */}
      <CreateDashboardModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={refreshSavedDashboards}
      />
    </div>
  );
};

export default Dashboard;
